
import { elem, styleInstaller } from "./chrome";

export interface WhisperActions {
  /** A name and the text meant for it -- never a session or entity id. The
   *  server alone decides whether that name exists and can hear it
   *  (`SendWhisper`, `net.SendWhisperPacket`, `uiwhisper.py:281-295`). */
  onSend?: (name: string, text: string) => void;
}

interface WhisperLine {
  readonly own: boolean;
  readonly notice: boolean;
  readonly text: string;
}

interface DialogHandle {
  readonly el: HTMLElement;
  readonly log: HTMLElement;
  readonly input: HTMLTextAreaElement;
  minimized: boolean;
}

/** `d:/ymir work/ui/public/`, the folder `whisperdialog.py` builds its buttons
 *  out of. */
const WHISPER_ART = "/ui/textures/public";

const STYLE_ID = "whisper-style";

const STYLE = `
.whisper-window {
  position: fixed;
  z-index: 61;
  width: 300px;
  height: 200px;
  min-width: 300px;
  min-height: 170px;
  max-width: 90vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  resize: both;
  overflow: hidden;
  border-radius: 8px;
  background: rgba(12, 14, 18, 0.92);
  border: 1px solid rgba(150, 220, 255, 0.28);
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.5);
  font: 12px/1.4 system-ui, sans-serif;
  color: #d8dee6;
  pointer-events: auto;
}
.whisper-window[hidden] { display: none; }
.whisper-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background: rgba(150, 220, 255, 0.1);
  border-bottom: 1px solid rgba(150, 220, 255, 0.18);
  cursor: default;
  flex: 0 0 auto;
}
.whisper-title { flex: 1; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.whisper-chrome {
  padding: 0;
  border: 0;
  /* The glyph stays for a reader; the button is its own sprite. */
  font-size: 0;
  color: transparent;
  cursor: pointer;
  background: no-repeat center;
}
/* minimizebutton and closebutton, in the three faces the dialog names
   (whisperdialog.py:132-134, :145-147). */
.whisper-minimize {
  width: 15px;
  height: 15px;
  background-image: url("${WHISPER_ART}/minimize_button_01.png");
}
.whisper-minimize:hover { background-image: url("${WHISPER_ART}/minimize_button_02.png"); }
.whisper-minimize:active { background-image: url("${WHISPER_ART}/minimize_button_03.png"); }
.whisper-close {
  width: 15px;
  height: 15px;
  background-image: url("${WHISPER_ART}/close_button_01.png");
}
.whisper-close:hover { background-image: url("${WHISPER_ART}/close_button_02.png"); }
.whisper-close:active { background-image: url("${WHISPER_ART}/close_button_03.png"); }
.whisper-log {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.whisper-line-own { color: #cfe2ff; }
.whisper-line-notice { color: #e0b04a; font-style: italic; }
.whisper-footer {
  display: flex;
  gap: 6px;
  padding: 6px 8px;
  border-top: 1px solid rgba(150, 220, 255, 0.18);
  flex: 0 0 auto;
}
.whisper-input {
  flex: 1;
  min-width: 0;
  resize: none;
  padding: 3px 6px;
  border-radius: 6px;
  border: 1px solid rgba(150, 200, 255, 0.24);
  background: rgba(0, 0, 0, 0.3);
  color: #d8dee6;
  font: inherit;
}
/* sendbutton, an xlarge_thin (whisperdialog.py:200-202); the sprite is 116x18. */
.whisper-send {
  width: 57px;
  height: 30px;
  padding: 0;
  border: 0;
  color: #ead7ae;
  font: 12px/30px Tahoma, "Segoe UI", sans-serif;
  cursor: pointer;
  background: url("${WHISPER_ART}/xlarge_thin_button_01.png") no-repeat center;
}
.whisper-send:hover { background-image: url("${WHISPER_ART}/xlarge_thin_button_02.png"); }
.whisper-send:active { background-image: url("${WHISPER_ART}/xlarge_thin_button_03.png"); }

/* The minimized column -- __ArrangeWhisperButton's stack, anchored to the
   same right edge (interfaceModule.py:1501-1513, screenWidth - 70). */
.whisper-tabs {
  position: fixed;
  right: 8px;
  top: 30%;
  z-index: 60;
  display: flex;
  flex-direction: column;
  gap: 6px;
  pointer-events: auto;
}
.whisper-tab {
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid rgba(150, 200, 255, 0.3);
  background: rgba(12, 14, 18, 0.88);
  color: #cfe2ff;
  font: 11px/1.2 system-ui, sans-serif;
  cursor: pointer;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.whisper-tab-unread {
  border-color: rgba(224, 176, 74, 0.8);
  background: rgba(224, 176, 74, 0.16);
  color: #ffdca0;
}
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

export type WhisperReceipt = "open" | "flashed" | "new-tab";

export class WhisperWindows {
  private readonly doc: Document;
  private readonly windows = new Map<string, DialogHandle>();
  private readonly tabs = new Map<string, HTMLButtonElement>();
  private readonly tabBar: HTMLElement;
  private readonly transcripts = new Map<string, WhisperLine[]>();

  /** How many dialogs have ever been cascaded, for the next one's offset --
   *  `windowOpenPosition`, stepped by 30px and wrapped every 5
   *  (`interfaceModule.py:1395-1396,1429-1430`). */
  private cascade = 0;

  constructor(
    private readonly container: HTMLElement,
    private readonly actions: WhisperActions = {},
  ) {
    this.doc = container.ownerDocument;
    installStyle(this.doc);

    this.tabBar = elem(this.doc, "div", "whisper-tabs");
    container.append(this.tabBar);
  }

  /** Open a dialog for `name`, or bring an existing one (minimized or not)
   *  back to the front -- `OpenWhisperDialog` / `ShowWhisperDialog`
   *  (`interfaceModule.py:1425-1436,1457-1469`). Always a name the caller
   *  already has (a selected friend, a target), never something typed here. */
  open(name: string): void {
    // A tab can exist with no window behind it at all -- a message received
    // from a name never yet opened (`RecvWhisper` making a bare button,
    // `interfaceModule.py:1438-1450`) -- so this always has to run, not only
    // on the "reopen a minimized dialog" branch below.
    this.dropTab(name);
    const existing = this.windows.get(name);
    if (existing) {
      existing.minimized = false;
      existing.el.hidden = false;
      this.renderLog(name);
      existing.input.focus();
      return;
    }
    this.create(name);
  }

  /** One line for `name`'s window; `notice` draws it as the system line a
   *  refused whisper is (`OnRecvWhisperError`, `game.py:869-874`). */
  receive(name: string, text: string, notice = false): WhisperReceipt {
    const win = this.windows.get(name);
    if (win && !win.minimized) {
      this.pushLine(name, { own: false, notice, text });
      return "open";
    }
    const hadTab = this.tabs.has(name);
    this.pushLine(name, { own: false, notice, text });
    if (hadTab) {
      this.flash(name);
      return "flashed";
    }
    if (!win) this.makeTab(name);
    this.flash(name);
    return "new-tab";
  }

  /** For tests and for callers that want to know what is on screen. */
  get openNames(): string[] {
    return [...this.windows.keys()].filter((name) => !this.windows.get(name)!.minimized);
  }

  get minimizedNames(): string[] {
    return [...this.tabs.keys()];
  }

  /** The lines drawn for `name`, own and received, in order. For tests. */
  transcript(name: string): string[] {
    return (this.transcripts.get(name) ?? []).map((line) => line.text);
  }

  private create(name: string): void {
    const el = elem(this.doc, "div", "whisper-window");
    el.dataset.name = name;
    const offset = 80 + (this.cascade % 5) * 30;
    this.cascade += 1;
    el.style.left = `${offset}px`;
    el.style.top = `${offset}px`;

    const header = elem(this.doc, "div", "whisper-header");
    const title = elem(this.doc, "span", "whisper-title", name);
    const minimize = this.doc.createElement("button");
    minimize.type = "button";
    minimize.className = "whisper-chrome whisper-minimize";
    minimize.textContent = "_";
    minimize.setAttribute("aria-label", "Minimize");
    minimize.addEventListener("click", () => this.minimize(name));
    const close = this.doc.createElement("button");
    close.type = "button";
    close.className = "whisper-chrome whisper-close";
    close.textContent = "×";
    close.setAttribute("aria-label", "Close");
    close.addEventListener("click", () => this.close(name));
    header.append(title, minimize, close);

    const log = elem(this.doc, "div", "whisper-log");

    const footer = elem(this.doc, "div", "whisper-footer");
    const input = elem(this.doc, "textarea", "whisper-input");
    input.rows = 1;
    // `chatLine.SetReturnEvent(SendWhisper)` / `SetEscapeEvent(Minimize)`
    // (`uiwhisper.py:103-105`): Enter sends, Shift+Enter is the multi-line
    // the original's `SetMultiLine()` also allows, Escape minimizes rather
    // than closing (a dialog you can reopen, not a conversation you lose).
    input.addEventListener("keydown", (event) => {
      const key = event as KeyboardEvent;
      if (key.key === "Enter" && !key.shiftKey) {
        key.preventDefault();
        this.send(name);
      } else if (key.key === "Escape") {
        key.preventDefault();
        this.minimize(name);
      }
    });
    const send = this.doc.createElement("button");
    send.type = "button";
    send.className = "whisper-send";
    // WHISPER_SEND (`locale_interface.txt:324` -- "Send").
    send.textContent = "Send";
    send.addEventListener("click", () => this.send(name));
    footer.append(input, send);

    el.append(header, log, footer);
    this.container.append(el);

    this.windows.set(name, { el, log, input, minimized: false });
    if (!this.transcripts.has(name)) this.transcripts.set(name, []);
    this.renderLog(name);
    input.focus();
  }

  private send(name: string): void {
    const win = this.windows.get(name);
    if (!win) return;
    const text = win.input.value.trim();
    if (text === "") return;
    win.input.value = "";
    // Drawn locally first, same reasoning as the main chat box's
    // `whisper-sent` line: this is this client's own record of what it
    // asked for, not a claim that it arrived (`uiwhisper.py:281-289` does
    // the same, appending into its own box before any answer is possible).
    this.pushLine(name, { own: true, notice: false, text });
    this.actions.onSend?.(name, text);
  }

  private minimize(name: string): void {
    const win = this.windows.get(name);
    if (!win) return;
    win.minimized = true;
    win.el.hidden = true;
    this.makeTab(name);
  }

  private close(name: string): void {
    const win = this.windows.get(name);
    if (win) win.el.remove();
    this.windows.delete(name);
    this.dropTab(name);
    // `Close()` calls `chat.ClearWhisper(self.targetName)`
    // (`uiwhisper.py:255-260`): the per-target buffer goes with the window.
    this.transcripts.delete(name);
  }

  private makeTab(name: string): void {
    if (this.tabs.has(name)) return;
    const tab = this.doc.createElement("button");
    tab.type = "button";
    tab.className = "whisper-tab";
    tab.dataset.name = name;
    tab.textContent = name;
    tab.addEventListener("click", () => this.open(name));
    this.tabs.set(name, tab);
    this.tabBar.append(tab);
  }

  private dropTab(name: string): void {
    const tab = this.tabs.get(name);
    if (!tab) return;
    tab.remove();
    this.tabs.delete(name);
  }

  private flash(name: string): void {
    this.tabs.get(name)?.classList.add("whisper-tab-unread");
  }

  private pushLine(name: string, line: WhisperLine): void {
    const list = this.transcripts.get(name) ?? [];
    list.push(line);
    this.transcripts.set(name, list);
    const win = this.windows.get(name);
    if (win && !win.minimized) this.renderLog(name);
  }

  private renderLog(name: string): void {
    const win = this.windows.get(name);
    if (!win) return;
    win.log.replaceChildren();
    for (const line of this.transcripts.get(name) ?? []) {
      const row = elem(
        this.doc,
        "div",
        line.notice ? "whisper-line-notice" : line.own ? "whisper-line-own" : "whisper-line",
        line.text,
      );
      win.log.append(row);
    }
    win.log.scrollTop = win.log.scrollHeight;
    // The tab that led back here is gone; whatever arrived while minimized
    // has just been rendered above, so no unread mark survives a reopen.
    this.tabs.get(name)?.classList.remove("whisper-tab-unread");
  }
}
